import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function GovernanceUpdatesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="65758" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[400px] w-full animate-pulse rounded-none bg-muted/40" />)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center rounded-none border border-destructive/20 bg-destructive/5 p-6 text-center">
        <p className="mb-4 text-muted-foreground font-sans">
          Unable to retrieve the latest dispatches at this time.
        </p>
        <Button variant="link" onClick={refetch} className="text-primary underline hover:text-primary/80 font-bold">
          Retry Connection
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex min-h-[200px] items-center justify-center rounded-none border border-border bg-muted/30 p-8 text-center">
        <p className="text-secondary font-sans">No news articles found in the database.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group relative flex h-full flex-col overflow-hidden border-border bg-background transition-all duration-200 hover:border-secondary hover:shadow-md rounded-none">
          {}
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
            {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" /> : <div className="flex h-full w-full items-center justify-center bg-muted/50 text-secondary/40">
                <span className="text-xs font-mono uppercase tracking-widest">Image Unavailable</span>
              </div>}
            
            {}
            <div className="absolute top-4 right-4 z-10">
              <Badge variant="secondary" className="bg-secondary text-primary-foreground font-mono text-[10px] tracking-tighter uppercase shadow-none rounded-none border-none hover:bg-secondary/90">
                <SafeHydrate><Calendar className="mr-1.5 h-3 w-3 text-primary" /></SafeHydrate>
                {post.date ? new Date(post.date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : 'Dispatch'}
              </Badge>
            </div>
          </div>

          <CardHeader className="pb-3">
            <CardTitle className="font-sans text-xl font-bold leading-tight tracking-tight text-secondary">
              <Link to={post.link || "#"} className="transition-colors hover:text-primary">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-grow pb-4">
            <div className="line-clamp-3 text-sm leading-relaxed text-secondary/80 font-sans" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />
          </CardContent>

          <CardFooter className="pt-0 border-t border-border/50 mt-4 py-4">
            <Link to={post.link || "#"} className="group/link inline-flex items-center text-xs font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary/80">
              Read Article
              <ArrowRight className="ml-2 h-3.5 w-3.5 opacity-0 -translate-x-2 transition-all duration-300 group-hover/link:translate-x-0 group-hover/link:opacity-100" />
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function LatestGovernanceUpdates() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc",
    ignore_sticky: true
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="articles-news" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end border-b border-secondary/20 pb-8">
          <div className="max-w-2xl">
            <h2 className="font-sans text-3xl font-extrabold tracking-tighter text-secondary uppercase sm:text-4xl md:text-5xl">Reports &amp; Articles</h2>
            <p className="mt-4 text-lg text-secondary/70 font-sans leading-relaxed">
              Real-time reporting on assessment irregularities, legislative updates, and tax reform efforts impacting New York homeowners.
            </p>
          </div>
          <Link to="/ArticlesAndNewsPage">
            <Button variant="outline" className="h-12 px-8 rounded-none border-secondary text-secondary font-bold uppercase tracking-widest hover:bg-secondary hover:text-white transition-all">VIEW ALL NEWS</Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc",
        ignore_sticky: true
      }} className="w-full">
          <GovernanceUpdatesConsumer />
        </PostsProvider>
      </div>
    </section>;
}